'use client'

import React, { useEffect, useState, useCallback } from "react";
import { useFormik, FormikErrors } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetUnitLoadHeightDetailsQuery, useCreateUnitLoadHeightsMutation } from '@/redux/slices/preship/preship-api-slice';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AlertTooltip from '@/components/alert-tooltip';
import { cn } from "@/lib/utils";
import { showSuccessToast, showErrorToast } from "../toast/toast";
import { Button } from "../ui/button";
import UnitLoadHeightConsolidatedView from "./UnitLoadHeightConsolidatedView";
import { UnitLoadHeightService } from "@/services/UnitLoadHeightService";
import EditBanner from "./EditBanner";
import { Info } from "lucide-react";

interface UnitLoadHeight {
  id?: number;
  variant: string;
  heightAB: string;
  heightBC: string;
  heightCD: string;
  heightDA: string;
}

interface PalletLpnNumber {
  id?: number;
  lpnNo: string;
  endStatusPallet: string;
  unitLoadHeights: UnitLoadHeight[];
}

interface FormValues {
  palletLpnNumbers: PalletLpnNumber[];
}

const validationSchema = Yup.object().shape({
  palletLpnNumbers: Yup.array().of(
    Yup.object().shape({
      lpnNo: Yup.string().required('LPN Number is required'),
      endStatusPallet: Yup.string().required('End Status is required'),
      unitLoadHeights: Yup.array().of(
        Yup.object().shape({
          heightAB: Yup.string().required('Height A-B is required'),
          heightBC: Yup.string().required('Height B-C is required'),
          heightCD: Yup.string().required('Height C-D is required'),
          heightDA: Yup.string().required('Height D-A is required'),
        })
      ),
    })
  ),
});

interface UnitLoadHeightProps {
  variant: 'preShip' | 'postShip' | 'postStack';
  week: string[];
  palletId?: number;
  tabName: string;
  stage: string;
  isEditing?: boolean;
  onFormChange?: (hasChanges: boolean) => void;
  isOffline?: boolean;
  onOfflineSubmit?: (values: any) => Promise<void>;
  setIsLoading: (isLoading: boolean) => void;
}

export default function UnitLoadHeight({
  variant,
  week,
  palletId,
  tabName,
  stage,
  onFormChange,
  isOffline,
  onOfflineSubmit,
  setIsLoading
}: UnitLoadHeightProps) {
  const { activePallet } = useSelector((state: RootState) => state.pallets);
  const [isDataReady, setIsDataReady] = useState(false);
  const [isConsolidatedViewOpen, setIsConsolidatedViewOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<FormValues | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { data: unitLoadHeightData, isLoading, error, refetch } = useGetUnitLoadHeightDetailsQuery({ palletId: activePallet?.id, variant }, {
    refetchOnMountOrArgChange: true
  });
  const [createUnitLoadHeights] = useCreateUnitLoadHeightsMutation();
  const [offlineUnitLoadHeightData, setOfflineUnitLoadHeightData] = useState<any>(null);
  const formik = useFormik<FormValues>({
    initialValues: {
      palletLpnNumbers: [],
    },
    // validationSchema,
    onSubmit: async (values) => {
      try {
        if (isOffline) {
          await UnitLoadHeightService.createUnitLoadHeights({
            palletId: Number(palletId),
            palletLpnNumbers: values.palletLpnNumbers,
          })
          showSuccessToast("Successfully saved unit load heights!");
          return;
        } else {
          console.log(values, 'values rannn')
          const payload = {
            palletId: activePallet?.id || 0,
            palletLpnNumbers: values.palletLpnNumbers.map(pln => ({
              ...pln,
              unitLoadHeights: pln.unitLoadHeights.map(ulh => ({
                ...ulh,
                heightAB: ulh.heightAB?.toString() || '',
                heightBC: ulh.heightBC?.toString() || '',
                heightCD: ulh.heightCD?.toString() || '',
                heightDA: ulh.heightDA?.toString() || '',
                variant: variant,
              })),
            })),
          };
          await createUnitLoadHeights(payload).unwrap();
          // refetch();
          showSuccessToast("Successfully saved unit load heights!");
        }
      } catch (error) {
        showErrorToast("Failed to save unit load heights");
        console.error('Failed to save unit load heights:', error);
      }
    },
  });

  useEffect(() => {
    if (isOffline) {
      const getData = async () => {
        try {
          const res = await UnitLoadHeightService.getUnitLoadHeightDetails(Number(palletId), variant)
          setOfflineUnitLoadHeightData(res)
        } catch (e) {
          console.log('error in getData')
          console.log(e)
        }
      }
      getData()
    }
  }, [isOffline, activePallet])
  useEffect(() => {
    if (activePallet && !isLoading && !isOffline) {
      let initialPalletLpnNumbers: PalletLpnNumber[] = [];

      if (error && 'status' in error && error.status === 404) {
        initialPalletLpnNumbers = Array.from({ length: Number(activePallet.numberOfPallets) }, (_, index): PalletLpnNumber => ({
          lpnNo: '',
          endStatusPallet: '',
          unitLoadHeights: [{
            variant: variant,
            heightAB: '',
            heightBC: '',
            heightCD: '',
            heightDA: '',
          }],
        }));
      } else if (unitLoadHeightData && unitLoadHeightData.palletLpnNumbers && unitLoadHeightData.palletLpnNumbers.length > 0) {
        initialPalletLpnNumbers = unitLoadHeightData.palletLpnNumbers.map((pln: any) => ({
          ...pln,
          endStatusPallet: pln.endStatusPallet || '', // Ensure endStatusPallet is not undefined
          unitLoadHeights: pln.unitLoadHeights.length > 0 ? pln.unitLoadHeights : [{
            variant: variant,
            heightAB: '',
            heightBC: '',
            heightCD: '',
            heightDA: '',
          }],
        }));
      }

      while (initialPalletLpnNumbers.length < Number(activePallet.numberOfPallets)) {
        initialPalletLpnNumbers.push({
          lpnNo: '',
          endStatusPallet: '',
          unitLoadHeights: [{
            variant: variant,
            heightAB: '',
            heightBC: '',
            heightCD: '',
            heightDA: '',
          }],
        });
      }

      const newValues = { palletLpnNumbers: initialPalletLpnNumbers };
      formik.setValues(newValues);
      setInitialValues(newValues);
      setIsDataReady(true);
    }
  }, [unitLoadHeightData, activePallet, error, variant, activePallet?.numberOfPallets, isLoading]);
  useEffect(() => {
    if (isOffline && offlineUnitLoadHeightData) {
      let initialPalletLpnNumbers: PalletLpnNumber[] = [];

      if (offlineUnitLoadHeightData && offlineUnitLoadHeightData.palletLpnNumbers && offlineUnitLoadHeightData.palletLpnNumbers.length > 0) {
        initialPalletLpnNumbers = offlineUnitLoadHeightData.palletLpnNumbers.map((pln: any) => ({
          ...pln,
          endStatusPallet: pln.endStatusPallet || '', // Ensure endStatusPallet is not undefined
          unitLoadHeights: pln.unitLoadHeights.length > 0 ? pln.unitLoadHeights : [{
            variant: variant,
            heightAB: '',
            heightBC: '',
            heightCD: '',
            heightDA: '',
          }],
        }));
      }

      while (initialPalletLpnNumbers.length < Number(activePallet?.numberOfPallets)) {
        initialPalletLpnNumbers.push({
          lpnNo: '',
          endStatusPallet: '',
          unitLoadHeights: [{
            variant: variant,
            heightAB: '',
            heightBC: '',
            heightCD: '',
            heightDA: '',
          }],
        });
      }

      const newValues = { palletLpnNumbers: initialPalletLpnNumbers };
      formik.setValues(newValues);
      setInitialValues(newValues);
      setIsDataReady(true);
    }
  }, [isOffline, offlineUnitLoadHeightData, activePallet]);

  useEffect(() => {
    if (onFormChange && initialValues) {
      const hasChanges = JSON.stringify(formik.values) !== JSON.stringify(initialValues);
      onFormChange(hasChanges);
    }
  }, [formik.values, onFormChange, initialValues]);
  useEffect(() => {
    setIsLoading(formik.isSubmitting);
  }, [formik.isSubmitting, setIsLoading])
  // if (isLoading) {
  //   return <div>Loading...</div>;
  // }
  console.log(formik.values, 'formik.values')
  const endStatusOptions = [
    "Pre Ship",
    "Post Ship",
    "Stack start(stacked+30m)",
    "Stack Week-1",
    "Stack Week-2",
    "Stack Week-3",
    "Stack Week-4 (unstacking+30M)"
  ];
  useEffect(() => {
    return () => {
      if (isEditing) {
        setIsEditing(false)
      }
    }
  }, [isEditing])

  // Add or update roundToNearestQuarter function to match the Lean component
  const roundToNearestQuarter = (value: string): string => {
    if (!value || isNaN(Number(value))) return value;
    const num = parseFloat(value);
    const rounded = Math.round(num * 4) / 4;
    return rounded.toFixed(2);
  };

  // Add keyboard navigation handler to move focus with arrow keys
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, rowIndex: number, colIndex: number) => {
    let targetRow = rowIndex;
    let targetCol = colIndex;
    switch (e.key) {
      case 'ArrowUp': targetRow = Math.max(0, rowIndex - 1); break;
      case 'Enter': // Adding Enter key to behave like Down arrow - Excel behavior
      case 'ArrowDown': targetRow = Math.min(formik.values.palletLpnNumbers.length - 1, rowIndex + 1); break;
      case 'ArrowLeft': targetCol = Math.max(1, colIndex - 1); break;
      case 'ArrowRight': targetCol = Math.min(5, colIndex + 1); break;
      default: return;
    }
    e.preventDefault();
    const nextEl = document.querySelector(`[data-row="${targetRow}"][data-col="${targetCol}"]`) as HTMLElement | null;
    nextEl?.focus();
  }, [formik.values.palletLpnNumbers.length]);

  return (
    <div className=" space-y-4">
      {!isOffline && <EditBanner
        stage={stage}
        palletId={palletId}
        component={tabName}
        onEditStateChange={setIsEditing}
      />}
      <form id={`unit-load-height-table-${activePallet?.id}`} onSubmit={formik.handleSubmit}>
        <Table className={`${(isEditing) || (isOffline) ? " " : "pointer-events-none"} border-collapse`}>
          <TableHeader>
            <TableRow>
              <TableHead className="border border-gray-300 text-blue p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left" colSpan={3}>
                Pallet Name: {activePallet?.name || 'N/A'} - Mode {activePallet?.shippingModes?.map((sp) => sp.mode).join(', ') || 'N/A'}
              </TableHead>
              <TableHead className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-center" colSpan={4}>
             { !isOffline &&   <div className='flex items-center gap-2 justify-between'>
                <span className="text-xs flex items-center gap-2 text-blue"><Info className="w-4 h-4" /> Note: Data will be rounded to the nearest 0.25 inch</span>
                  {/* {variant === 'preShip' ? 'Pre Ship' : variant === 'postShip' ? 'Post Ship' : ''} Unit Load Height */}
                  <div className="flex-center gap-4 !pointer-events-auto">
                    {/* <Button type='button' className="bg-white text-md font-semibold text-blue border-2 border-blue">
                    Clear All
                  </Button> */}
                    <Button
                      onClick={() => setIsConsolidatedViewOpen(true)}
                      type='button' className="bg-white hover:bg-blue hover:text-white text-md font-semibold text-blue border-2 border-blue">
                      Consolidated View
                    </Button>
                  </div>
                </div>}
              </TableHead>
            </TableRow>
            <TableRow>
              {["Pallet No", "Pallet Identifier", "End Status", "A-B (inch)", "B-C (inch)", "C-D (inch)", "D-A (inch)"].map((head) => (
                <TableHead
                  key={head}
                  className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left"
                >
                  {head}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {formik.values.palletLpnNumbers.map((pln, index) => {
              const isRowEditable = week.includes(pln.endStatusPallet);
              return (
                <TableRow key={index}>
                  <TableCell className="border border-gray-300 bg-states-disabled p-0">
                    <Input
                      className="w-full h-9 border-none"
                      value={index + 1}
                      readOnly
                    />
                  </TableCell>
                  <TableCell className="border border-gray-300 bg-states-disabled p-0">
                    <div className="relative">
                      <Input
                        className="w-full  border-none "
                        // placeholder="Enter Pallet Identifier"
                        {...formik.getFieldProps(`palletLpnNumbers.${index}.lpnNo`)}
                        readOnly={!isRowEditable}
                      />
                      {formik.touched.palletLpnNumbers &&
                        Array.isArray(formik.touched.palletLpnNumbers) &&
                        formik.touched.palletLpnNumbers[index]?.lpnNo &&
                        formik.errors.palletLpnNumbers &&
                        Array.isArray(formik.errors.palletLpnNumbers) &&
                        typeof formik.errors.palletLpnNumbers[index] === 'object' &&
                        formik.errors.palletLpnNumbers[index] !== null &&
                        'lpnNo' in formik.errors.palletLpnNumbers[index] && (
                          <AlertTooltip message={formik.errors.palletLpnNumbers[index].lpnNo as string} />
                        )}
                    </div>
                  </TableCell>
                  <TableCell className="border border-gray-300 p-0">
                    <div className="relative">
                      <Select
                        value={pln.endStatusPallet}
                        onValueChange={(value) => formik.setFieldValue(`palletLpnNumbers.${index}.endStatusPallet`, value)}
                      >
                        <SelectTrigger
                          data-row={index} data-col={1}
                          onKeyDown={(e) => handleKeyDown(e, index, 1)}
                          className="w-full rounded-none bg-transparent border-2 border-required focus:border-blue"
                        >
                          <SelectValue placeholder="Select End Status" />
                        </SelectTrigger>
                        <SelectContent>
                          {endStatusOptions.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formik.touched.palletLpnNumbers &&
                        Array.isArray(formik.touched.palletLpnNumbers) &&
                        formik.touched.palletLpnNumbers[index]?.endStatusPallet &&
                        formik.errors.palletLpnNumbers &&
                        Array.isArray(formik.errors.palletLpnNumbers) &&
                        typeof formik.errors.palletLpnNumbers[index] === 'object' &&
                        formik.errors.palletLpnNumbers[index] !== null &&
                        'endStatusPallet' in formik.errors.palletLpnNumbers[index] && (
                          <AlertTooltip message={formik.errors.palletLpnNumbers[index].endStatusPallet as string} />
                        )}
                    </div>
                  </TableCell>
                  {['heightAB', 'heightBC', 'heightCD', 'heightDA'].map((field, colIndex) => (
                    <TableCell key={field} className="border border-gray-300 p-0">
                      <div className="relative">
                        <Input
                          data-row={index} data-col={2 + colIndex}
                          onKeyDown={(e) => handleKeyDown(e, index, 2 + colIndex)}
                          className={cn("w-full h-9 border-none", !isRowEditable && "bg-gray-200")}
                          type="number"
                          step="0.25"
                          placeholder="Enter"
                          value={formik.values.palletLpnNumbers[index].unitLoadHeights[0][field] as string}
                          onChange={(e) => {
                            // Allow free typing without rounding during input
                            if (isRowEditable) {
                              formik.setFieldValue(`palletLpnNumbers.${index}.unitLoadHeights.0.${field}`, e.target.value);
                            }
                          }}
                          onBlur={(e) => {
                            // Only round when user finishes typing (on blur)
                            if (isRowEditable && e.target.value) {
                              const roundedValue = roundToNearestQuarter(e.target.value);
                              formik.setFieldValue(`palletLpnNumbers.${index}.unitLoadHeights.0.${field}`, roundedValue);
                            }
                          }}
                          readOnly={!isRowEditable}
                          disabled={!isRowEditable}
                        />
                        {
                          formik.touched.palletLpnNumbers &&
                          Array.isArray(formik.touched.palletLpnNumbers) &&
                          formik.touched.palletLpnNumbers[index]?.unitLoadHeights &&
                          Array.isArray(formik.touched.palletLpnNumbers[index].unitLoadHeights) &&
                          formik.touched.palletLpnNumbers[index].unitLoadHeights[colIndex] &&
                          formik.errors.palletLpnNumbers &&
                          Array.isArray(formik.errors.palletLpnNumbers) &&
                          typeof formik.errors.palletLpnNumbers[index] === 'object' &&
                          formik.errors.palletLpnNumbers[index] !== null &&
                          field in formik.errors.palletLpnNumbers[index] &&
                          formik.errors.palletLpnNumbers[index][field] && (
                            <AlertTooltip message={formik.errors.palletLpnNumbers[index][field] as string} />
                          )
                        }
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <button type="submit" style={{ display: 'none' }} title="Submit Unit Load Height Data"></button>
        <UnitLoadHeightConsolidatedView
          isOpen={isConsolidatedViewOpen}
          onClose={() => setIsConsolidatedViewOpen(false)}
          variant={variant}
        />
      </form>

    </div>
  );
}